// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Tabel › Typer's fold-out (frontend-r22.24): a type row opens to show its
 * parts inline as sub-rows — the live ones by code, then the type's parts
 * marked as an error ("Fejldetektion", with Gendan). A large type (hundreds
 * of parts) shows a page at a time behind "Vis flere". The open types are
 * remembered per project for the session (sessionStorage); the keyboard
 * walks the visible rows as a tree grid: ↑/↓ move, → opens (or steps into),
 * ← closes (or steps out to the type).
 */

import type { SurveyPart, SurveyType } from '../api/types';
import { roomName, type DismissedPart } from '../kortlaegning/model';
import { storeyById, storeyOfRoom, type Storey } from './storeys';

/** Sub-rows a type shows at first, and how many more each "Vis flere" adds. */
export const FOLDOUT_PAGE = 50;

/** One sub-row: a part of the type, live or marked as an error. */
export interface SubRow {
  part: SurveyPart;
  dismissed: boolean;
}

/**
 * The type's sub-rows under the room focus: live parts by code, then its
 * dismissed ones (from `CaseData.dismissed`) by code.
 */
export function typeSubRows(type: SurveyType, dismissed: readonly DismissedPart[], room: number | null): SubRow[] {
  const inRoom = (p: SurveyPart) => room === null || p.room_id === room;
  const live = type.parts
    .filter(inRoom)
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((part) => ({ part, dismissed: false }));
  const gone = dismissed
    .filter((d) => d.type.id === type.id && inRoom(d.part))
    .map((d) => d.part)
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((part) => ({ part, dismissed: true }));
  return [...live, ...gone];
}

/**
 * Where a sub-row's part is: its room and storey (`Rum 2 · 1. sal`), leaving
 * out what it lacks. The stored storey (`storey_id`) leads; else the storey
 * its room stands on.
 */
export function partPlace(part: SurveyPart, storeys: readonly Storey[]): string {
  const stored = part.storey_id != null ? storeyById(storeys, part.storey_id) : null;
  const storey = stored ?? storeyOfRoom(storeys, part.room_id);
  return [roomName(part), storey?.name ?? ''].filter((s) => s).join(' · ');
}

/** How many of `total` sub-rows show with `limit` requested (the first page when unset). */
export function shownCount(total: number, limit: number | undefined): number {
  return Math.min(total, Math.max(FOLDOUT_PAGE, limit ?? FOLDOUT_PAGE));
}

/** The "Vis flere" label: how many come next, of how many still hidden. */
export function moreLabel(total: number, shown: number): string | null {
  const hidden = total - shown;
  if (hidden <= 0) return null;
  const next = Math.min(FOLDOUT_PAGE, hidden);
  return next === hidden ? `Vis ${hidden} flere` : `Vis ${next} flere (${hidden} skjult)`;
}

// -------------------------------------------------------------- expanded --

/** `ids` with `id` toggled. */
export function toggleExpanded(ids: ReadonlySet<number>, id: number): Set<number> {
  const next = new Set(ids);
  if (!next.delete(id)) next.add(id);
  return next;
}

/** "Fold alle ud" when any listed type is closed, else "Fold alle ind". */
export function allExpanded(ids: ReadonlySet<number>, typeIds: readonly number[]): boolean {
  return typeIds.length > 0 && typeIds.every((id) => ids.has(id));
}

/**
 * Fold every listed type out, or (when all already are) in. Types not in
 * the list (filtered away) keep their state.
 */
export function foldAll(ids: ReadonlySet<number>, typeIds: readonly number[]): Set<number> {
  const next = new Set(ids);
  if (allExpanded(ids, typeIds)) for (const id of typeIds) next.delete(id);
  else for (const id of typeIds) next.add(id);
  return next;
}

export const FOLDOUT_STORAGE_PREFIX = 'rux.tabel.foldout:';

export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): SessionStorageLike | undefined {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/** The open types remembered for `projectKey` this session; missing or garbled storage is none. */
export function readExpanded(projectKey: string, storage = defaultStorage()): Set<number> {
  try {
    const raw = storage?.getItem(FOLDOUT_STORAGE_PREFIX + projectKey);
    if (!raw) return new Set();
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return new Set();
    return new Set(list.filter((v): v is number => Number.isInteger(v) && v > 0));
  } catch {
    return new Set();
  }
}

export function writeExpanded(projectKey: string, ids: ReadonlySet<number>, storage = defaultStorage()): void {
  try {
    storage?.setItem(FOLDOUT_STORAGE_PREFIX + projectKey, JSON.stringify([...ids].sort((a, b) => a - b)));
  } catch {
    // A refused write only means the fold-out is not remembered.
  }
}

// -------------------------------------------------------------- keyboard --

/** One focusable row of the table, in display order. */
export type RowKey = { kind: 'type'; typeId: number } | { kind: 'part'; typeId: number; code: string };

export function rowKeyId(k: RowKey): string {
  return k.kind === 'type' ? `t:${k.typeId}` : `p:${k.typeId}:${k.code}`;
}

/** The visible rows in order: each type, then (when open) its shown sub-rows. */
export function visibleRowKeys(
  types: readonly SurveyType[],
  expanded: ReadonlySet<number>,
  subRows: (type: SurveyType) => readonly SubRow[],
  limits: ReadonlyMap<number, number>,
): RowKey[] {
  const out: RowKey[] = [];
  for (const t of types) {
    out.push({ kind: 'type', typeId: t.id });
    if (!expanded.has(t.id)) continue;
    const rows = subRows(t);
    const n = shownCount(rows.length, limits.get(t.id));
    for (let i = 0; i < n; i++) out.push({ kind: 'part', typeId: t.id, code: rows[i].part.code });
  }
  return out;
}

export type FoldoutKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End';

export interface FoldoutMove {
  /** The row to focus next (null: stay). */
  focus: RowKey | null;
  /** A type to open or close (null: none). */
  toggle: number | null;
}

/**
 * What a key does on row `at` of `rows` (tree-grid semantics): ↑/↓ the
 * previous/next visible row, Home/End the first/last; → on a closed type
 * with parts opens it, on an open one steps to its first part; ← on an open
 * type closes it, on a part steps out to its type. Null: not ours.
 */
export function foldoutKey(
  key: string,
  at: RowKey,
  rows: readonly RowKey[],
  expanded: ReadonlySet<number>,
  hasParts: (typeId: number) => boolean,
): FoldoutMove | null {
  const i = rows.findIndex((r) => rowKeyId(r) === rowKeyId(at));
  if (i < 0) return null;
  switch (key) {
    case 'ArrowDown':
      return { focus: rows[i + 1] ?? null, toggle: null };
    case 'ArrowUp':
      return { focus: i > 0 ? rows[i - 1] : null, toggle: null };
    case 'Home':
      return { focus: i > 0 ? rows[0] : null, toggle: null };
    case 'End':
      return { focus: i < rows.length - 1 ? rows[rows.length - 1] : null, toggle: null };
    case 'ArrowRight':
      if (at.kind !== 'type' || !hasParts(at.typeId)) return { focus: null, toggle: null };
      if (!expanded.has(at.typeId)) return { focus: null, toggle: at.typeId };
      return { focus: rows[i + 1]?.kind === 'part' ? rows[i + 1] : null, toggle: null };
    case 'ArrowLeft':
      if (at.kind === 'part') return { focus: { kind: 'type', typeId: at.typeId }, toggle: null };
      return { focus: null, toggle: expanded.has(at.typeId) ? at.typeId : null };
    default:
      return null;
  }
}
